import { BoundaryCharacteristic, Markdown } from '@cadstrom/sigma-engine-shared';
import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useState } from 'react';

import { DirectiveApplyBar } from './directive-apply-bar.js';
import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

export interface ParameterDirectiveRow {
	kind: Markdown.ParametersAndModesRowKind.Parameter;
	name: string;
	value?: string | null;
	variance?: string | null;
	units?: string | null;
}

export interface ModePreconditionDirectiveRow {
	characteristic: string;
	signalName: string;
	relativeSignalName?: string | null;
	minimum?: number | null;
	nominal?: number | null;
	maximum?: number | null;
}

export interface ModeDirectiveRow {
	kind: Markdown.ParametersAndModesRowKind.Mode;
	modeId: string;
	description?: string | null;
	preconditions?: ModePreconditionDirectiveRow[];
}

export type ParametersAndModesDirectiveRow = ParameterDirectiveRow | ModeDirectiveRow;

export interface ParametersAndModesTableMarkdownItemProps {
	/** Raw JSON string encoding an array of {@link ParametersAndModesDirectiveRow} entries. */
	jsonData?: string;
}

const DASH = '—';

const editInputClass =
	'w-full min-w-10 bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

const characteristics: string[] = Object.values(BoundaryCharacteristic);

/**
 * Markdown directive component that renders a package's sigma-model parameters and its operating
 * modes inside a workbench message.
 *
 * Parses `jsonData` as `Array<ParametersAndModesDirectiveRow>` — a mixed array discriminated by
 * `kind` — and renders each kind as its own section: parameters as name/value/variance/units, modes
 * as an id and the numerical preconditions that make the mode active. Each section renders only
 * when the payload carries rows of that kind, and applies through its own extraction context, so a
 * table carrying only one kind behaves exactly like a single-purpose directive table. Rows are
 * editable inline before applying. Renders `null` when `jsonData` is absent or not valid JSON.
 *
 * @param jsonData - Raw JSON string containing the parameters-and-modes array.
 */
export function ParametersAndModesTableMarkdownItem({ jsonData }: ParametersAndModesTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseParametersAndModes);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	// Each entry keeps the index it holds in the mixed array, so an edit in either
	// section writes back to the row the reader is looking at.
	const parameterEntries = rows.flatMap((row, index) => (isParameterRow(row) ? [{ row, index }] : []));
	const modeEntries = rows.flatMap((row, index) => (isModeRow(row) ? [{ row, index }] : []));

	function updateParameter(index: number, patch: Partial<ParameterDirectiveRow>) {
		setRows((prev) =>
			prev ? prev.map((row, i) => (i === index && isParameterRow(row) ? { ...row, ...patch } : row)) : null,
		);
	}

	function updateMode(index: number, patch: Partial<ModeDirectiveRow>) {
		setRows((prev) =>
			prev ? prev.map((row, i) => (i === index && isModeRow(row) ? { ...row, ...patch } : row)) : null,
		);
	}

	function updatePrecondition(index: number, preconditionIndex: number, patch: Partial<ModePreconditionDirectiveRow>) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === index && isModeRow(row) ?
						{
							...row,
							preconditions: (row.preconditions ?? []).map((precondition, j) =>
								j === preconditionIndex ? { ...precondition, ...patch } : precondition,
							),
						}
					:	row,
				)
			:	null,
		);
	}

	function removePrecondition(index: number, preconditionIndex: number) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === index && isModeRow(row) ?
						{ ...row, preconditions: (row.preconditions ?? []).filter((_, j) => j !== preconditionIndex) }
					:	row,
				)
			:	null,
		);
	}

	function addPrecondition(index: number) {
		setRows((prev) =>
			prev ?
				prev.map((row, i) =>
					i === index && isModeRow(row) ?
						{
							...row,
							preconditions: [
								...(row.preconditions ?? []),
								{ characteristic: BoundaryCharacteristic.Voltage, signalName: '' },
							],
						}
					:	row,
				)
			:	null,
		);
	}

	const editToggle = (
		<ButtonBase
			className='text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
			onClick={() => setIsEditing((v) => !v)}
		>
			{isEditing ? 'Done' : 'Edit'}
		</ButtonBase>
	);

	return (
		<>
			{parameterEntries.length > 0 && (
				<>
					<div className='my-1 overflow-x-auto'>
						<table className='w-full border-collapse text-sm'>
							<thead>
								<tr className='border-b border-gray-a4'>
									<th className='min-w-32 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>
										Parameter
									</th>
									<th className='min-w-20 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Value</th>
									<th className='min-w-20 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>
										Variance
									</th>
									<th className='min-w-14 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Units</th>
									<th className='px-2 py-1 text-right'>{editToggle}</th>
								</tr>
							</thead>
							<tbody>
								{parameterEntries.map(({ row, index }) => (
									<tr
										key={index}
										className={clsx('border-b border-gray-a3', index % 2 === 0 ? 'bg-transparent' : 'bg-gray-a2')}
									>
										<td className='px-2 py-1 font-mono whitespace-nowrap text-gray-12'>
											{isEditing ?
												<input
													type='text'
													value={row.name}
													onChange={(e) => updateParameter(index, { name: e.target.value })}
													className={editInputClass}
												/>
											:	row.name}
										</td>
										<td className='px-2 py-1 text-gray-12'>
											{isEditing ?
												<input
													type='text'
													value={row.value ?? ''}
													onChange={(e) => updateParameter(index, { value: e.target.value || null })}
													className={editInputClass}
												/>
											:	(row.value ?? DASH)}
										</td>
										<td className='px-2 py-1 text-gray-12'>
											{isEditing ?
												<input
													type='text'
													value={row.variance ?? ''}
													onChange={(e) => updateParameter(index, { variance: e.target.value || null })}
													className={editInputClass}
												/>
											:	(row.variance ?? DASH)}
										</td>
										<td className='px-2 py-1 text-gray-11'>
											{isEditing ?
												<input
													type='text'
													value={row.units ?? ''}
													onChange={(e) => updateParameter(index, { units: e.target.value || null })}
													className={editInputClass}
												/>
											:	(row.units ?? DASH)}
										</td>
										<td />
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<DirectiveApplyBar data={parameterEntries.map(({ row }) => row)} configId='sigma-model-parameters' />
				</>
			)}

			{modeEntries.length > 0 && (
				<>
					<div className='my-1 overflow-x-auto'>
						<table className='w-full border-collapse text-sm'>
							<thead>
								<tr className='border-b border-gray-a4'>
									<th className='min-w-28 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Mode</th>
									<th className='min-w-56 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>
										Active when
									</th>
									<th className='min-w-32 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Notes</th>
									<th className='px-2 py-1 text-right'>{parameterEntries.length === 0 ? editToggle : null}</th>
								</tr>
							</thead>
							<tbody>
								{modeEntries.map(({ row, index }) => (
									<tr
										key={index}
										className={clsx('border-b border-gray-a3', index % 2 === 0 ? 'bg-transparent' : 'bg-gray-a2')}
									>
										<td className='px-2 py-1 align-top font-mono whitespace-nowrap text-gray-12'>
											{isEditing ?
												<input
													type='text'
													value={row.modeId}
													onChange={(e) => updateMode(index, { modeId: e.target.value })}
													className={editInputClass}
												/>
											:	row.modeId}
										</td>
										<td className='px-2 py-1 align-top text-gray-12'>
											{isEditing ?
												<div className='flex flex-col gap-1'>
													{(row.preconditions ?? []).map((precondition, preconditionIndex) => (
														<div key={preconditionIndex} className='flex items-center gap-1'>
															<input
																type='text'
																placeholder='signal'
																value={precondition.signalName}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, { signalName: e.target.value })
																}
																className={clsx(editInputClass, 'w-24')}
															/>
															<input
																type='text'
																placeholder='rel. to'
																value={precondition.relativeSignalName ?? ''}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, {
																		relativeSignalName: e.target.value || null,
																	})
																}
																className={clsx(editInputClass, 'w-24')}
															/>
															<select
																value={precondition.characteristic}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, { characteristic: e.target.value })
																}
																className={clsx(editInputClass, 'w-28')}
															>
																{/* Keeping an unrecognized characteristic selectable shows what arrived, rather
																    than letting the cell read as the first valid option. */}
																{!characteristics.includes(precondition.characteristic) && (
																	<option value={precondition.characteristic}>
																		{precondition.characteristic} (not a characteristic)
																	</option>
																)}
																{characteristics.map((characteristic) => (
																	<option key={characteristic} value={characteristic}>
																		{characteristic}
																	</option>
																))}
															</select>
															<input
																type='number'
																placeholder='min'
																value={precondition.minimum ?? ''}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, {
																		minimum: parseNumericInput(e.target.value),
																	})
																}
																className={clsx(editInputClass, 'w-16')}
															/>
															<input
																type='number'
																placeholder='nom'
																value={precondition.nominal ?? ''}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, {
																		nominal: parseNumericInput(e.target.value),
																	})
																}
																className={clsx(editInputClass, 'w-16')}
															/>
															<input
																type='number'
																placeholder='max'
																value={precondition.maximum ?? ''}
																onChange={(e) =>
																	updatePrecondition(index, preconditionIndex, {
																		maximum: parseNumericInput(e.target.value),
																	})
																}
																className={clsx(editInputClass, 'w-16')}
															/>
															<ButtonBase
																aria-label='Remove precondition'
																className='px-1 text-gray-11 hover:text-gray-12'
																onClick={() => removePrecondition(index, preconditionIndex)}
															>
																✕
															</ButtonBase>
														</div>
													))}
													<ButtonBase
														className='self-start text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
														onClick={() => addPrecondition(index)}
													>
														+ Add precondition
													</ButtonBase>
												</div>
											: (row.preconditions ?? []).length === 0 ?
												DASH
											:	<div className='flex flex-col gap-0.5'>
													{(row.preconditions ?? []).map((precondition, preconditionIndex) => (
														<div key={preconditionIndex} className='flex flex-wrap items-baseline gap-1'>
															<span className='font-mono text-gray-12'>{precondition.signalName}</span>
															{precondition.relativeSignalName ?
																<span className='text-gray-11'>
																	rel. <span className='font-mono'>{precondition.relativeSignalName}</span>
																</span>
															:	null}
															<span className='text-gray-11'>{precondition.characteristic}</span>
															<span className='text-gray-12'>{formatPreconditionRange(precondition)}</span>
														</div>
													))}
												</div>
											}
										</td>
										<td className='px-2 py-1 align-top text-gray-12'>
											{isEditing ?
												<input
													type='text'
													value={row.description ?? ''}
													onChange={(e) => updateMode(index, { description: e.target.value || null })}
													className={editInputClass}
												/>
											:	(row.description ?? DASH)}
										</td>
										<td />
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<DirectiveApplyBar data={modeEntries.map(({ row }) => row)} configId='package-modes' />
				</>
			)}
		</>
	);
}

function isParameterRow(row: ParametersAndModesDirectiveRow): row is ParameterDirectiveRow {
	return row.kind === Markdown.ParametersAndModesRowKind.Parameter;
}

function isModeRow(row: ParametersAndModesDirectiveRow): row is ModeDirectiveRow {
	return row.kind === Markdown.ParametersAndModesRowKind.Mode;
}

function parseNumericInput(value: string): number | null {
	if (value === '') {
		return null;
	}
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

/**
 * Renders a precondition's bounds as `min … nominal … max`, with a dash standing in for
 * each bound the datasheet does not give.
 *
 * @param precondition - The precondition to format.
 * @returns The bounds as one display string.
 */
function formatPreconditionRange(precondition: ModePreconditionDirectiveRow): string {
	return [precondition.minimum, precondition.nominal, precondition.maximum]
		.map((bound) => (bound === null || bound === undefined ? DASH : String(bound)))
		.join(' … ');
}

/**
 * Parses the directive body into the rows the table renders, keeping the two kinds in the
 * order the message wrote them.
 *
 * @param jsonData - Raw JSON body of the directive.
 * @returns The rows, or null when the body is not a JSON array carrying at least one row of
 *   either kind.
 */
function parseParametersAndModes(jsonData: string): ParametersAndModesDirectiveRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const rows = parsed.filter((item): item is ParametersAndModesDirectiveRow => {
			if (typeof item !== 'object' || item === null) {
				return false;
			}
			const candidate = item as Record<string, unknown>;
			if (candidate['kind'] === Markdown.ParametersAndModesRowKind.Parameter) {
				return (
					typeof candidate['name'] === 'string' &&
					isOptionalString(candidate['value']) &&
					isOptionalString(candidate['variance']) &&
					isOptionalString(candidate['units'])
				);
			}
			return (
				candidate['kind'] === Markdown.ParametersAndModesRowKind.Mode &&
				typeof candidate['modeId'] === 'string' &&
				isOptionalString(candidate['description']) &&
				isPreconditionList(candidate['preconditions'])
			);
		});
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Whether a value is a string or absent.
 *
 * @param value - The value to test.
 * @returns True when the value is a string, null, or undefined.
 */
function isOptionalString(value: unknown): boolean {
	return value === null || value === undefined || typeof value === 'string';
}

/**
 * Whether a value is a number or absent.
 *
 * @param value - The value to test.
 * @returns True when the value is a number, null, or undefined.
 */
function isOptionalNumber(value: unknown): boolean {
	return value === null || value === undefined || typeof value === 'number';
}

/**
 * Whether a value is a usable precondition list.
 *
 * Every field the table reads is checked, because a row that reaches the render with a
 * non-array `preconditions`, or an entry missing its signal, throws while mapping and takes
 * the whole message down with it.
 *
 * @param value - The value to test.
 * @returns True when the value is absent, or an array whose every entry is a precondition.
 */
function isPreconditionList(value: unknown): boolean {
	if (value === null || value === undefined) {
		return true;
	}
	if (!Array.isArray(value)) {
		return false;
	}
	return value.every((entry) => {
		if (typeof entry !== 'object' || entry === null) {
			return false;
		}
		const precondition = entry as Record<string, unknown>;
		return (
			typeof precondition['characteristic'] === 'string' &&
			typeof precondition['signalName'] === 'string' &&
			isOptionalString(precondition['relativeSignalName']) &&
			isOptionalNumber(precondition['minimum']) &&
			isOptionalNumber(precondition['nominal']) &&
			isOptionalNumber(precondition['maximum'])
		);
	});
}
